import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { modules } from "byzantine";
import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import {
  BackCancelButton,
  CollapsibleSection,
  FormHeaderWithIcon,
} from "cerulean";
import useReviewableAction from "../../../useReviewableAction";
import LoanSelectorSection from "./sections/LoanSelectorSection";
import PaymentTypeSection from "./sections/PaymentTypeSection";
import PaymentDetailsSection from "./sections/PaymentDetailsSection";
import LoanPaymentReview from "./LoanPaymentReview";
import LoanPaymentReceipt from "./LoanPaymentReceipt";
import type { LoanReceiptData } from "./LoanPaymentReceipt";

const { useLoanPaymentForm, useLoanAutopay } = modules.loanPayment;
interface LoanPaymentFormProps {
  limits: object;
}

const LoanPaymentForm = ({ limits }: LoanPaymentFormProps) => {
  const { l10n } = useLocalization();
  const { form, formMetadata } = useLoanPaymentForm();
  const { autopayData } = useLoanAutopay(form.values.toAccountId);
  const { isInput, goToInput, goToReview } = useReviewableAction();
  const navigate = useNavigate();
  const [receiptData, setReceiptData] = useState<LoanReceiptData | null>(null);

  const currentStep = useMemo(() => {
    if (formMetadata.isPaymentDetailsVisible) {
      return 3;
    }
    if (formMetadata.isSelectPaymentTypeVisible) {
      return 2;
    }
    return 1;
  }, [
    formMetadata.isPaymentDetailsVisible,
    formMetadata.isSelectPaymentTypeVisible,
  ]);

  const handleCancel = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    const submission = await form.submitForm();
    if (submission.success) {
      goToReview();
    }
  }, [form, goToReview]);

  if (receiptData) {
    return <LoanPaymentReceipt data={receiptData} />;
  }

  if (isInput) {
    return (
      <div className="loan-payment-page">
        <FormHeaderWithIcon
          headerText={l10n.getString("loan-paydown-form-page-heading")}
        />

        {/* Section 1: Select Loan */}
        <CollapsibleSection
          title={l10n.getString("loan-paydown-step-1-title")}
          currentStep={currentStep}
          sectionStep={1}
          totalSteps={3}
          tagName={l10n.getString("loan-paydown-step-x-of-y-tag", {
            sectionStep: 1,
            totalSteps: 3,
          })}
        >
          <LoanSelectorSection form={form} />
        </CollapsibleSection>

        {/* Section 2: Payment Type */}
        <CollapsibleSection
          title={l10n.getString("loan-paydown-step-2-title")}
          currentStep={currentStep}
          sectionStep={2}
          totalSteps={3}
          tagName={l10n.getString("loan-paydown-step-x-of-y-tag", {
            sectionStep: 2,
            totalSteps: 3,
          })}
        >
          <PaymentTypeSection form={form} />
        </CollapsibleSection>

        {/* Section 3: Payment Details */}
        <CollapsibleSection
          title={l10n.getString("loan-paydown-step-3-title")}
          currentStep={currentStep}
          sectionStep={3}
          totalSteps={3}
          tagName={l10n.getString("loan-paydown-step-x-of-y-tag", {
            sectionStep: 3,
            totalSteps: 3,
          })}
        >
          <PaymentDetailsSection
            form={form}
            limits={limits}
            formMetadata={formMetadata}
          />
        </CollapsibleSection>

        <div className="alignChild--center--center form-buttons-with-spacing">
          <BackCancelButton
            onBack={handleCancel}
            backLabel={l10n.getString("loan-paydown-cancel-button")}
          />
          <div style={{ marginLeft: "auto" }}>
            <Button
              label={l10n.getString("loan-paydown-review-button")}
              testId="review-loan-paydown-payment"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <LoanPaymentReview
      form={form}
      goBack={goToInput}
      onSuccess={setReceiptData}
      autopayData={autopayData}
    />
  );
};

export default LoanPaymentForm;
